import { useMemo, useState } from 'react';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
//
import {
  Alert,
  Button,
  Card,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
//
import { FormProvider, RHFTextField } from '@/components/hook-form';
import {
  ProviderEditTimeOffSkeleton,
  TimeOffOneOffRequest,
  TimeOffRecurringRequest,
} from '..';
import { SectionTitle } from '@/features/users/shared/components';
import DialogSimple from '@/components/dialog/DialogSimple';
// api
import {
  createProviderTimeOff,
  getProviderDayOff,
  previewProviderTimeOffAffectedSessions,
  updateProviderTimeOff,
} from '../../api';
import { getSettingsData } from '@/features/app/api';
import {
  bulkUnassignBookingSessions,
  updateBookingSession,
} from '@/features/bookings-management/api';
import {
  getActiveProviders,
  getProvidersTimeOff,
} from '@/features/users/shared/api';
import { showClientCleanerRestrictionWarnings } from '@/features/bookings-management/utils';
// date utils
import { addMinutes, format } from 'date-fns';
// utils
import {
  formatDaysOfWeekFromApi,
  formatRepeatString,
  localZoominEffext,
  TIME_OFF_DEFAULT_VALUES,
} from '../../utils';
// types
import { TimeOffFormValues } from '../../types';
import { BookingSession } from '@/features/bookings-management/types';
import { ProviderTimeOff } from '@/features/users/shared/types';

type ReqProps = {
  editMode?: boolean;
  currentTimeOffId?: string | null;
  exitEditMode: () => void;
};

function parseDateValue(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function getSessionDateRange(session: BookingSession): {
  start: Date | null;
  end: Date | null;
} {
  const timeString = session.start_time || session.time;
  const sessionStart = parseDateValue(
    timeString ? `${session.date}T${timeString}` : session.date
  );

  if (!sessionStart) {
    return { start: null, end: null };
  }

  let sessionEnd = session.end_time
    ? parseDateValue(`${session.date}T${session.end_time}`)
    : null;

  if (!sessionEnd) {
    const baseDuration = Number(session.total_hours ?? session.hours ?? 0);
    const extraDuration = Number(session.extra_hours ?? 0);
    const durationMinutes = Math.max((baseDuration + extraDuration) * 60, 30);
    sessionEnd = addMinutes(sessionStart, durationMinutes);
  }

  return { start: sessionStart, end: sessionEnd };
}

// =======================================================

export default function ProviderTimeOffRequest({
  editMode,
  currentTimeOffId,
  exitEditMode,
}: ReqProps) {
  const { userId } = useParams();
  const { successMsg, errorMsg, warningMsg } = useSnackbarMsg();

  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictingSessions, setConflictingSessions] = useState<
    BookingSession[]
  >([]);
  const [conflictingSessionsCount, setConflictingSessionsCount] = useState(0);
  const [pendingSubmission, setPendingSubmission] = useState<{
    mode: 'create' | 'update';
    dto: any;
  } | null>(null);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  const [replacementCleanerId, setReplacementCleanerId] = useState<string>('');
  const [isResolvingConflict, setIsResolvingConflict] = useState(false);

  // =================
  //  REACT HOOK FORM
  // =================

  const methods = useForm({
    defaultValues: TIME_OFF_DEFAULT_VALUES,
  });
  const { control, reset, handleSubmit } = methods;

  const { request_type_id, recurrence_type } = useWatch({
    control,
  });

  const providerId = Number(userId);

  const { data: activeProviders = [] } = useQuery({
    queryKey: ['activeProviders'],
    queryFn: getActiveProviders,
    enabled: providerId > 0,
    onError: (error) =>
      errorMsg(error, 'Something went wrong while fetching cleaners'),
  });

  const canReassignSingleConflict =
    conflictingSessionsCount === 1 && conflictingSessions.length === 1;
  const conflictSession = canReassignSingleConflict
    ? conflictingSessions[0]
    : null;

  const conflictRange = useMemo(() => {
    if (!conflictSession) return null;
    const { start: sessionStart, end: sessionEnd } =
      getSessionDateRange(conflictSession);
    if (!sessionStart || !sessionEnd) return null;
    return {
      sessionStart,
      sessionEnd,
      startDate: format(sessionStart, 'yyyy-MM-dd'),
      endDate: format(sessionEnd, 'yyyy-MM-dd'),
    };
  }, [conflictSession]);

  const { data: providersTimeOff = [], isLoading: isProvidersTimeOffLoading } =
    useQuery<ProviderTimeOff[]>({
      queryKey: [
        'providersTimeOff-for-conflict',
        conflictRange?.startDate,
        conflictRange?.endDate,
      ],
      queryFn: () =>
        getProvidersTimeOff({
          startDate: conflictRange?.startDate,
          endDate: conflictRange?.endDate,
        }),
      enabled: Boolean(conflictRange),
    });

  const availableReplacementCleaners = useMemo(() => {
    if (!canReassignSingleConflict || !conflictRange) return [];
    const { sessionStart, sessionEnd } = conflictRange;
    if (!sessionStart || !sessionEnd) return [];

    return activeProviders.filter((provider) => {
      if (provider.id === providerId) return false;
      if (!providersTimeOff.length) return true;

      return !providersTimeOff.some((timeOff) => {
        if (timeOff.provider?.id !== provider.id) return false;
        const timeOffStart = parseDateValue(timeOff.start_date);
        const timeOffEnd = parseDateValue(timeOff.end_date);
        if (!timeOffStart || !timeOffEnd) return false;
        return sessionStart < timeOffEnd && sessionEnd > timeOffStart;
      });
    });
  }, [
    activeProviders,
    canReassignSingleConflict,
    conflictRange,
    providerId,
    providersTimeOff,
  ]);

  // =================
  //      QUERIES
  // =================

  const queryClient = useQueryClient();

  // FETCH
  const { isLoading: isTimeOffDataLoading } = useQuery({
    queryKey: ['providerTimeoff', currentTimeOffId],
    queryFn: () => getProviderDayOff(String(userId), String(currentTimeOffId)),
    onSuccess: (data) => {
      reset({
        request_type_id: String(data.type_id),
        start_date: data.start_date,
        end_date: data.end_date,
        recurrence_type: data.offday_recurrence ? 'Recurring' : 'One-off',
        timeoff_request_notes: data.notes,
        recurrence_days_of_week: data.offday_recurrence
          ? formatDaysOfWeekFromApi(data.offday_recurrence.week_days)
          : [],
        recurrence_repeat_string: formatRepeatString(data.offday_recurrence),
        recurrence_from_date: data?.offday_recurrence
          ? data?.offday_recurrence.from_date
          : '',
        // TODO: test this
        recurrence_until:
          data?.offday_recurrence?.until === null
            ? ''
            : data?.offday_recurrence?.until,
      });
    },
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching days off`);
    },
    enabled: !!userId && !!currentTimeOffId,
  });

  // POST
  const createTimeOffRequest = useMutation({
    mutationFn: ({ id, timeoffBaseDto }: { id: string; timeoffBaseDto: any }) =>
      createProviderTimeOff(id, timeoffBaseDto),
    onSuccess: () => {
      queryClient.invalidateQueries(['providerTimeoff', userId]);
      successMsg(`New request successfully created`);
      setPendingSubmission(null);
      setConflictingSessions([]);
      setConflictingSessionsCount(0);
      setConflictDialogOpen(false);
      handleExitEditMode();
    },
    onError: (error) => {
      errorMsg(error, `There was an error while submitting request`);
    },
  });

  // PUT
  const updateTimeOffRequest = useMutation({
    mutationFn: ({
      userId,
      timeOffId,
      timeoffBaseDto,
    }: {
      userId: string;
      timeOffId: string;
      timeoffBaseDto: any;
    }) => updateProviderTimeOff(userId, timeOffId, timeoffBaseDto),
    onSuccess: () => {
      queryClient.invalidateQueries(['providerTimeoff', userId]);
      successMsg(`Request successfully updated`);
      setPendingSubmission(null);
      setConflictingSessions([]);
      setConflictingSessionsCount(0);
      setConflictDialogOpen(false);
      handleExitEditMode();
    },
    onError: (error) => {
      errorMsg(error, `There was an error while updating request`);
    },
  });

  const submitTimeOff = ({
    mode,
    dto,
  }: {
    mode: 'create' | 'update';
    dto: any;
  }) => {
    if (!userId) return;

    if (mode === 'update') {
      if (!currentTimeOffId) return;
      updateTimeOffRequest.mutate({
        userId: String(userId),
        timeOffId: String(currentTimeOffId),
        timeoffBaseDto: dto,
      });
    } else {
      createTimeOffRequest.mutate({ id: String(userId), timeoffBaseDto: dto });
    }
  };

  const handleConflictDialogClose = () => {
    setConflictDialogOpen(false);
    setConflictingSessions([]);
    setConflictingSessionsCount(0);
    setPendingSubmission(null);
    setReplacementCleanerId('');
  };

  const unassignSessions = async (sessions: BookingSession[]) => {
    const sessionIds = Array.from(
      new Set(sessions.map((session) => session.id))
    );
    if (!sessionIds.length) return;

    const response = await bulkUnassignBookingSessions(sessionIds);
    if (response.failed_sessions?.length) {
      throw new Error(
        `Failed to unassign ${response.failed_sessions.length} booking session(s)`
      );
    }
  };

  const handleProceedDespiteConflicts = async () => {
    if (!pendingSubmission) return;

    setIsResolvingConflict(true);
    try {
      if (conflictingSessions.length) {
        await unassignSessions(conflictingSessions);
        successMsg('Booking session(s) unassigned successfully');
      }
      submitTimeOff(pendingSubmission);
      setConflictDialogOpen(false);
      setConflictingSessions([]);
      setConflictingSessionsCount(0);
      setPendingSubmission(null);
      setReplacementCleanerId('');
    } catch (error) {
      errorMsg(error, 'Failed to unassign booking sessions');
    } finally {
      setIsResolvingConflict(false);
    }
  };

  const handleAssignAndProceed = async () => {
    if (
      !pendingSubmission ||
      !conflictingSessions.length ||
      !replacementCleanerId
    ) {
      return;
    }

    const targetSession = conflictingSessions[0];
    setIsResolvingConflict(true);
    try {
      const updatedSession = await updateBookingSession(targetSession.id, {
        booking_cleaners: [Number(replacementCleanerId)],
      });
      successMsg('Session reassigned to selected cleaner');
      showClientCleanerRestrictionWarnings(
        updatedSession,
        warningMsg,
        activeProviders
      );
      setReplacementCleanerId('');
      setConflictingSessions([]);
      setConflictingSessionsCount(0);
      setConflictDialogOpen(false);
      submitTimeOff(pendingSubmission);
    } catch (error) {
      errorMsg(error, 'Failed to reassign booking session');
    } finally {
      setIsResolvingConflict(false);
    }
  };

  // SETTINGS
  const { data: timeOffRequestTypes, isLoading: isTimeOffRequestTypesLoading } =
    useQuery({
      queryKey: ['settings'],
      queryFn: () => getSettingsData(),
      onError: (error) => {
        errorMsg(
          error,
          `Something went wrong while fetching time-off request types`
        );
      },
      select: (data) => data?.offDaysRequestTypes,
    });

  // ================
  //      SUBMIT
  // ================

  const onSubmitTimeoffRequest = async (data: TimeOffFormValues) => {
    const timeoffBaseDto = {
      status: 'approved', // other options: 'rejected', 'pending'
      type_id: Number(data.request_type_id),
      start_date: data.start_date,
      end_date: data.end_date,
      notes: data.timeoff_request_notes,
    };
    if (recurrence_type === 'Recurring') {
      const formatted_repeat_string =
        data.recurrence_repeat_string.toLowerCase() === 'fortnightly'
          ? 'weekly'
          : data.recurrence_repeat_string.toLowerCase();
      Object.assign(timeoffBaseDto, {
        offday_recurrence: {
          repeat_string: formatted_repeat_string,
          week_days: data.recurrence_days_of_week
            .map((day) => day.recurrence_short_name)
            .join(', '),
          from_date: data.recurrence_from_date,
          until: !!data.recurrence_until ? data.recurrence_until : null,
          interval_number:
            data.recurrence_repeat_string.toLowerCase() === 'fortnightly'
              ? 2
              : 1,
        },
      });
    }
    try {
      setIsCheckingConflicts(true);
      const preview = await previewProviderTimeOffAffectedSessions(
        String(userId),
        timeoffBaseDto
      );
      const conflicts = preview.affected_sessions ?? [];
      const previewCount = Number(preview.count);
      const conflictCount = Number.isFinite(previewCount)
        ? previewCount
        : conflicts.length;
      setIsCheckingConflicts(false);

      if (conflictCount > 0) {
        setConflictingSessions(conflicts);
        setConflictingSessionsCount(conflictCount);
        setReplacementCleanerId('');
        setPendingSubmission({
          mode: editMode ? 'update' : 'create',
          dto: timeoffBaseDto,
        });
        setConflictDialogOpen(true);
        return;
      }

      setConflictingSessions([]);
      setConflictingSessionsCount(0);
      submitTimeOff({
        mode: editMode ? 'update' : 'create',
        dto: timeoffBaseDto,
      });
    } catch (error) {
      setIsCheckingConflicts(false);
      errorMsg(error, 'Unable to verify active booking sessions');
    }
  };
  const onFormError = (errors: any) => console.error(errors);

  const handleExitEditMode = () => {
    exitEditMode();
    reset(TIME_OFF_DEFAULT_VALUES);
  };

  const isMutationLoading =
    createTimeOffRequest.isLoading || updateTimeOffRequest.isLoading;
  const isSubmitting = isMutationLoading || isCheckingConflicts;
  const conflictTitle =
    conflictingSessionsCount === 1
      ? 'Cleaner has 1 booked session'
      : `Cleaner has ${conflictingSessionsCount} booked sessions`;
  const isReplacementOptionLoading = isProvidersTimeOffLoading;

  // =========================

  return (
    <FormProvider
      methods={methods}
      onSubmit={handleSubmit(onSubmitTimeoffRequest, onFormError)}
    >
      <Stack
        direction="column"
        spacing={3}
        component={Card}
        sx={{
          p: 2,
          minWidth: 250,
          border: editMode ? '2px solid blue' : 'none',
          animation: editMode ? `${localZoominEffext} 1s` : 'none',
          animationDelay: editMode ? '0.5s' : 'none',
        }}
      >
        <SectionTitle title={editMode ? 'Edit Time-off' : 'Request Time-off'} />

        {editMode && isTimeOffDataLoading ? (
          <ProviderEditTimeOffSkeleton />
        ) : (
          <>
            {/* REQUEST TYPE */}
            {!isTimeOffRequestTypesLoading ? (
              <RHFTextField label="Request Type" name="request_type_id" select>
                {(timeOffRequestTypes || []).map((option) => (
                  // id: 1 - Holiday, 2 - Not available, 3 - Sick leave
                  <MenuItem key={option.id} value={option.id}>
                    {option.description}
                  </MenuItem>
                ))}
              </RHFTextField>
            ) : (
              <Skeleton variant="rounded" height={40} />
            )}

            {/* HOLIDAY */}
            {(Number(request_type_id) === 1 ||
              Number(request_type_id) === 3) && <TimeOffOneOffRequest />}

            {/* NOT AVAILABLE */}
            {Number(request_type_id) === 2 && (
              <>
                <RHFTextField
                  label="Recurrence type"
                  name="recurrence_type"
                  select
                >
                  {['One-off', 'Recurring'].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </RHFTextField>
                {recurrence_type === 'One-off' && <TimeOffOneOffRequest />}
                {recurrence_type === 'Recurring' && <TimeOffRecurringRequest />}
              </>
            )}

            <Stack direction="column" spacing={1}>
              {editMode && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => handleExitEditMode()}
                >
                  Cancel
                </Button>
              )}
              <LoadingButton
                variant="contained"
                type="submit"
                loading={isSubmitting}
              >
                {editMode ? 'Save changes' : 'Submit Request'}
              </LoadingButton>
            </Stack>
          </>
        )}
      </Stack>
      <DialogSimple
        dialogTitle={conflictTitle}
        open={conflictDialogOpen}
        onClickButtonClose={handleConflictDialogClose}
        loadingButtonText="Continue and unassign"
        loadingButtonColor="warning"
        loadingButtonType="button"
        onClickButtonLoading={handleProceedDespiteConflicts}
        isSubmitting={isMutationLoading || isResolvingConflict}
        agreeButton={false}
        loadingButton
        disableLoadingButton={isResolvingConflict}
      >
        <Stack spacing={2} sx={{ px: 3, pb: 3 }}>
          <Alert severity="warning">
            This provider has existing booking sessions in the selected period.
            Continuing will unassign all affected booking sessions.
          </Alert>
          <List dense>
            {conflictingSessions.map((session) => {
              const { start: sessionStart, end: sessionEnd } =
                getSessionDateRange(session);
              const startLabel = sessionStart
                ? format(sessionStart, 'PPP p')
                : 'Unknown start';
              const endLabel = sessionEnd
                ? format(sessionEnd, 'PPP p')
                : 'Unknown end';
              const bookingLabel = session.booking_id
                ? `Booking #${session.booking_id}`
                : 'Booking ID unavailable';
              return (
                <ListItem key={session.id} disableGutters>
                  <ListItemText
                    primary={`Session #${session.id} • ${bookingLabel}`}
                    secondary={`From ${startLabel} to ${endLabel}`}
                  />
                </ListItem>
              );
            })}
          </List>
          {canReassignSingleConflict && (
            <Stack spacing={2}>
              <Alert severity="info">
                Assign the booking session to another cleaner to keep it
                scheduled.
              </Alert>
              {isReplacementOptionLoading ? (
                <Skeleton variant="rounded" height={48} />
              ) : availableReplacementCleaners.length ? (
                <>
                  <TextField
                    select
                    label="Replacement cleaner"
                    value={replacementCleanerId}
                    onChange={(event) =>
                      setReplacementCleanerId(event.target.value)
                    }
                    fullWidth
                  >
                    {availableReplacementCleaners.map((cleaner) => (
                      <MenuItem key={cleaner.id} value={cleaner.id}>
                        {`${cleaner.first_name} ${cleaner.last_name}`}
                      </MenuItem>
                    ))}
                  </TextField>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={handleAssignAndProceed}
                    disabled={
                      !replacementCleanerId || isReplacementOptionLoading
                    }
                    loading={isResolvingConflict}
                  >
                    Assign to selected cleaner and continue
                  </LoadingButton>
                </>
              ) : (
                <Alert severity="info">
                  No other cleaners are currently available for reassignment.
                </Alert>
              )}
            </Stack>
          )}
        </Stack>
      </DialogSimple>
    </FormProvider>
  );
}
